/** Listing Info compliance: lease / sale required forms. */

export const LISTING_COMPLIANCE_DEAL_OPTIONS = [
  { value: "", label: "— Select lease or sale —" },
  { value: "lease", label: "Lease" },
  { value: "sale", label: "Sale" },
];

const LEASE_MANDATORY = [
  { id: "listing_agreement", label: "Listing agreement" },
  { id: "mls_data_sheet", label: "MLS data sheet" },
  { id: "reco", label: "RECO" },
  { id: "appointment_instruction", label: "Appointment instruction" },
  { id: "mls_listing_sheet", label: "MLS Listing Sheet" },
];

const LEASE_OPTIONAL = [
  { id: "amendment_listing", label: "Amendment to listing", removable: true },
];

const SALE_MANDATORY = [
  { id: "listing_agreement", label: "Listing agreement" },
  { id: "mls_data_sheet", label: "MLS data sheet" },
  { id: "reco", label: "RECO" },
  { id: "fintrac", label: "FINTRAC" },
  { id: "appointment_instruction", label: "Appointment instruction" },
  { id: "mls_listing_sheet", label: "MLS Listing Sheet" },
];

const SALE_OPTIONAL = [
  { id: "amendment_listing", label: "Amendment to listing", removable: true },
];

export const LISTING_COMPLIANCE_BY_DEAL = {
  lease: { mandatory: LEASE_MANDATORY, optional: LEASE_OPTIONAL },
  sale: { mandatory: SALE_MANDATORY, optional: SALE_OPTIONAL },
};

export function normalizeListingDealType(value) {
  const v = String(value ?? "").trim().toLowerCase();
  if (v === "lease" || v === "sale") return v;
  return "";
}

function presetRowsForDeal(dealType) {
  const type = normalizeListingDealType(dealType);
  const config = LISTING_COMPLIANCE_BY_DEAL[type];
  if (!config) return [];
  const mandatory = (config.mandatory || []).map((p) => ({
    ...p,
    group: "mandatory",
    preset: true,
    removable: false,
  }));
  const optional = (config.optional || []).map((p) => ({
    ...p,
    group: "optional",
    preset: true,
    removable: true,
  }));
  return [...mandatory, ...optional];
}

/** Merge saved checklist with presets for the selected deal type. */
export function normalizeListingComplianceChecklist(
  raw,
  dealType,
  removedPresetIds = [],
) {
  const type = normalizeListingDealType(dealType);
  const removed = new Set((removedPresetIds || []).map(String));
  const presets = presetRowsForDeal(type).filter((p) => !removed.has(p.id));
  const saved = Array.isArray(raw) ? raw : [];
  const byId = new Map(
    saved.filter((i) => i && i.id).map((i) => [String(i.id), i]),
  );
  const presetIds = new Set(presets.map((p) => p.id));

  const items = presets.map((preset) => ({
    id: preset.id,
    label: preset.label,
    checked: !!byId.get(preset.id)?.checked,
    preset: true,
    removable: !!preset.removable,
    group: preset.group,
  }));

  for (const item of saved) {
    if (!item?.id || presetIds.has(String(item.id))) continue;
    const label = String(item.label || "").trim();
    if (!label) continue;
    items.push({
      id: String(item.id),
      label,
      checked: !!item.checked,
      preset: false,
      removable: true,
      group: "custom",
    });
  }

  return items;
}

export function canRemoveListingComplianceItem(item) {
  if (!item) return false;
  if (!item.preset) return true;
  return !!item.removable;
}

/** Completed / Pending / No from checklist ticks. */
export function listingComplianceStatusFromChecklist(items) {
  const list = items || [];
  if (list.length === 0) return "No";
  if (list.every((i) => i.checked)) return "Completed";
  if (list.some((i) => i.checked)) return "Pending";
  return "No";
}

export function listingComplianceStatusForListing(listing) {
  const c = listing?.compliance;
  if (!c) return "No";
  const dealType = normalizeListingDealType(c.dealType);
  const items = normalizeListingComplianceChecklist(
    c.checklist,
    dealType,
    c.removedPresetIds,
  );
  if (items.length === 0) return "No";
  return listingComplianceStatusFromChecklist(items);
}

export function newListingComplianceItemId() {
  return `listing-custom-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}
